/* import __COLOCATED_TEMPLATE__ from './make-offer-modal.hbs'; */
import { debounce } from '@ember/runloop';
import EmberObject, { observer, computed } from '@ember/object';
import { on } from '@ember/object/evented';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { closeModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal, clearErrors, setErrors } from 'sh-www/utils/handles-errors';
import { task } from 'ember-concurrency';

/**
 * Use this component to get an offer between a buyer and seller.
 */
const MakeOfferModal = Component.extend({
  tagName: '',

  /** Register services. */
  applicationCommandService : service('commands/application-commands'),
  store: service(),
  session: service(),
  userCommandService: service('commands/user-commands'),

  currentUser: readOnly('session.currentUser'),

  /**
   * The model for this modal
   *
   * @param {Object}
   */
  model: {
    /**
     * @param {Application}
     */
    application: null,

    /**
     * True if this should pose information as a counter offer.
     *
     * @param {Boolean=}
     */
    makingCounterOffer: false,

    /**
     * Who we're sending the offer to.
     *
     * @param {String}
     */
    offerRecipientName: '',

    /**
     * Will be called after the price has been confirmed.
     *
     * @param {Function}
     */
    onPriceConfirmed: (price) => {
      throw new Error('must define onPriceConfirmed');
    },

    /**
     * True if this should just prompt for approval
     *
     * @param {Boolean=}
     */
    isApproveOffer: false,

    /**
     * @param {Boolean}   True if this offer is coming from the seller
     */
    isFromSeller: false,

    /**
     * The initial payout amount to populate the modal with.
     *
     * @param {Float=}
     */
    initialPayoutAmount: 0,
  },

  /** @type {Ember.Object|null} The test billing record. */
  _testBillingRecord: null,

  /** @type {Ember.Object|null} The fee structure without covered fees. */
  _agnosticFeeStructure: null,

  /** @type {Boolean} Whether the test billing record is currently being requested. */
  _isUpdatingTestBillingRecord: false,

  /** Fetches the test billing record from the server. */
  _updateTestBillingRecord() {
    const application = this.get('model.application');

    if (!application) return;

    const userType = this.get('model.isFromSeller') ? 'seller' : 'buyer';

    const existingOffer = this.get('model.initialPayoutAmount');
    const newOfferExists = this.get('_offerValue') !== null;
    const newOffer = this.get('_offerValueFloat');
    const price = newOfferExists ? newOffer : existingOffer || 0;

    return this.get('applicationCommandService')
      .getTestBillingRecord({ application, userType, price, recalculateFees: true })
      .then((response) => {
        const store = this.get('store');
        store.pushPayload(response);
        const testBillingRecord = store.peekRecord(
          this.get('model.isFromSeller')  ? 'shPayment' : 'shInvoice',
          response.data.id
        );

        this.set('_testBillingRecord', testBillingRecord);
      })
      .then(() => {
        if (this.currentUser.isBuyer && application.sellerFeesCovered) {
          return this.loadHiringFeeStructure.perform(application.seller.id, this.currentUser.company.id, price);
        }
      })
      .catch((error) => {
        if (this.isDestroying) {
          return;
        }

        const errorMessage = EmberObject.create(error).get('payload.data.attributes.body');

        this.setProperties({
          _testBillingRecord: null,
          _agnosticFeeStructure: null,
        });

        // Handle unknown errors with the general modal.
        if (!errorMessage) {
          const uiMessage = 'There was an error calculating the payment. Please try again.';
          const debugMessage = `updateTestBillingRecord -- app id: ${application.get('id')}`;
          handleErrorResponseWithModal(this, error, uiMessage, debugMessage);
        }
      })
      .finally(() => {
        if (this.isDestroying) {
          return;
        }

        this.set('_isUpdatingTestBillingRecord', false);
      });
  },

  /**
   * When the model changes (on initialization mainly), we'll clean up our state.
   */
  _observeModel: on('init', observer('model', function() {
    if (!this.get('model')) return;

    this.set('_testBillingRecord', null);
    this.set('_agnosticFeeStructure', null);
    this.set('_isUpdatingTestBillingRecord', false);
    this.set('_offerValue', this.get('model.initialPayoutAmount') || null);

    this.set('_isUpdatingTestBillingRecord', true);
    debounce(this, this._updateTestBillingRecord, 750);
  })),

  /**
   * The value from the user entering the text input field.
   *
   * @type {Float|null}
   */
  _offerValue: null,

  /**
   * The user's input parsed as a float.
   *
   * @type {Float}
   */
  _offerValueFloat: computed('_offerValue', function() {
    const offerValue = parseFloat(this.get('_offerValue'));

    if (!offerValue) {
      return 0;
    }

    return offerValue;
  }),

  /**
   * Formatted string of the freelancer payout amount.
   *
   * @type {String}
   */
  _payoutFormatted: computed('_offerValueFloat', function() {
    return '$' + parseFloat(this.get('_offerValueFloat')).toFixed(2);
  }),

  _confirmOfferText: computed('model.{isApproveOffer,sendQuote}', function () {
    if (this.model.sendQuote) {
      return 'Send quote';
    } else if (this.model.isApproveOffer) {
      return 'Approve offer';
    } else {
      return 'Make offer';
    }
  }),

  /** @type {Boolean} Whether the submit button is disabled. */
  _isSubmitDisabled: computed(
    '_offerValueFloat',
    'model.initialPayoutAmount',
    'model.isApproveOffer',
    function () {
      // Always enabled for approval.
      if (this.get('model.isApproveOffer')) return false;

      // Disabled if the offer is 0 or null.
      if (!this.get('_offerValueFloat')) return true;

      // Disabled if the offer is the same as the initial amount.
      return this.get('_offerValueFloat') === this.get('model.initialPayoutAmount');
    },
  ),

  loadHiringFeeStructure: task(function *(sellerId, companyId, amount) {
    try {
      const response = yield this.userCommandService.getHiringFeeStructure(
        sellerId,
        companyId,
        amount,
      );

      this.set('_agnosticFeeStructure', response);
    } catch (error) {
      handleErrorResponseWithModal(this, error);
    }
  }).drop(),

  /**
   * @inheritDoc
   */
  actions: {
    /** Called when the user confirms the amount. */
    onConfirmOffer: function(e) {
      const offerValue = this.get('_offerValueFloat');

      clearErrors(this);

      if (!offerValue) {
        setErrors(this, { 'client-exception': 'Your offer is too low.' }, MakeOfferModal.FORM_FIELDS);
        return;
      }

      closeModal(this);
      this.get('model.onPriceConfirmed')(offerValue);
    },

    /** Calls the method that updates the test billing record. */
    update(text) {
      this.set('_offerValue', text);
      this.set('_isUpdatingTestBillingRecord', true);
      debounce(this, this._updateTestBillingRecord, 750);
    },

    closeModal() {
      closeModal(this);
    },
  },
});

MakeOfferModal.reopenClass({
  FORM_FIELDS: [
    'client-exception',
  ],
});

export default MakeOfferModal;
